<template>
  <v-container>
    <div class="d-flex flex-column flex-md-row">
      <!-- ------------ Sidebar (Desktop) ------------ -->
      <div class="sidebar the-thin-grey-border flex-column d-none d-md-flex flex-1">
        <!-- Add @change listener -->
        <v-tabs v-model="tab" vertical @change="changeTab">
          <v-tab>
            Exceptions
            <v-chip small class="ml-2" outlined>{{ getCountText(0) }}</v-chip>
          </v-tab>
          <v-tab>
            GOOD Pages
            <v-chip small class="ml-2" outlined>{{ getCountText(1) }}</v-chip>
          </v-tab>
          <v-tab>
            BAD Pages
             <v-chip small class="ml-2" outlined>{{ getCountText(2) }}</v-chip>
          </v-tab>
          <v-tab>
            Unclassified
            <v-chip small class="ml-2" outlined>{{ getCountText(3) }}</v-chip>
          </v-tab>
        </v-tabs>
      </div>
      <!-- ------------ Top Bar (Mobile) ------------ -->
      <div class="d-flex the-thin-grey-border flex-row d-md-none">
         <!-- Add @change listener -->
        <v-tabs v-model="tab" @change="changeTab">
           <!-- Added counts to mobile tabs too -->
          <v-tab>
            Exc <v-chip x-small class="ml-1" outlined>{{ getCountText(0) }}</v-chip>
          </v-tab>
          <v-tab>
            Good <v-chip x-small class="ml-1" outlined>{{ getCountText(1) }}</v-chip>
          </v-tab>
          <v-tab>
            Bad <v-chip x-small class="ml-1" outlined>{{ getCountText(2) }}</v-chip>
          </v-tab>
          <v-tab>
            Uncl <v-chip x-small class="ml-1" outlined>{{ getCountText(3) }}</v-chip>
          </v-tab>
        </v-tabs>
      </div>
      <!-- ------------ Main Content ------------ -->
      <div class="d-flex the-thin-grey-border flex-column flex-grow-1 ml-md-4 mt-md-0 mt-4 pa-3">
        <!-- Status Display -->
        <p v-if="loading && !documents.length" class="text-center">Loading documents...</p>
        <p v-else-if="!loading && documents.length">
          Showing record {{ currentIndex + 1 }} of {{ documents.length }} loaded.
          <span v-if="filteredTotalCounts[tab] !== null && filteredTotalCounts[tab] !== 'Loading...' && filteredTotalCounts[tab] !== 'Error'">
            Filtered total: {{ filteredTotalCounts[tab] }} {{ getTabNoun(tab) }} documents.
          </span>
          <span v-else-if="filteredTotalCounts[tab] === 'Loading...'">
            (Loading filtered count...)
          </span>
           <span v-else-if="filteredTotalCounts[tab] === 'Error'">
            (Error loading count)
          </span>
          Total in DB: {{ totalCount }}.
        </p>
        <!-- Message when no documents are found for the category -->
        <p v-else-if="!loading && !documents.length && allLoaded && filteredTotalCounts[tab] === 0">
          No {{ getTabNoun(tab) }} documents found in this category. Total in DB: {{ totalCount }}
        </p>
        <!-- Message when all loaded for the category, but some might have existed -->
         <p v-else-if="!loading && !documents.length && allLoaded">
           Finished reviewing all {{ getTabNoun(tab) }} documents in this category. Total in DB: {{ totalCount }}
         </p>
         <!-- Message when no documents loaded yet, but not finished -->
         <p v-else-if="!loading && !documents.length && !allLoaded && filteredTotalCounts[tab] !== 'Loading...'">
           No {{ getTabNoun(tab) }} documents loaded yet. Total in DB: {{ totalCount }}
         </p>

        <!-- Image Review Area -->
        <div v-if="currentDocument" class="d-flex flex-column align-center justify-center mt-4">
          <p class="mb-1 text-caption">
            Doc ID: <strong>{{ currentDocument.id }}</strong> |
            Page ID: <strong>{{ currentDocument.page_id || 'N/A' }}</strong> |
            Job ID: <strong>{{ currentDocument.job_id || 'N/A' }}</strong>
          </p>
           <p class="mb-2 text-caption">
             Current DB Classification: <strong>{{ displayClassification(currentDocument) }}</strong>
             <span v-if="currentDocument.review"> | Marked as <strong>Exception</strong></span>
           </p>

          <!-- Image Area -->
          <div class="image-container d-flex align-center justify-center">
            <!-- Show spinner ONLY when actively loading -->
            <v-progress-circular
              v-if="currentDocument && preloadQueue[currentDocument.id] === 'loading'"
              indeterminate
              color="primary"
              class="my-10"
            ></v-progress-circular>

            <!-- Show image if URL is available and not explicitly marked as error in queue -->
            <!-- Use :key to help Vue force re-render if src changes to the same value for a different doc initially -->
            <v-img
              v-else-if="currentImage"
              :key="currentDocument.id"
              :src="currentImage"
              class="review-image"
              contain
              @load="onImageLoadSuccess(currentDocument.id)"
              @error="onImageError(currentDocument.id)"
            ></v-img>

             <!-- Show error placeholder if specifically marked as error OR v-img failed -->
            <div v-else-if="imageError || (currentDocument && preloadQueue[currentDocument.id] === 'error')" class="image-error-placeholder red--text">
                <v-icon color="red" large>mdi-image-off</v-icon>
                <p class="mt-2">Error loading image</p>
                <p v-if="currentDocument && currentDocument.page_id" class="text-caption">Path: operations/switchorders/pages/{{ currentDocument.page_id }}.jpg</p>
                <p v-else-if="currentDocument" class="text-caption">Missing Page ID</p>
            </div>

            <!-- Show pending/unavailable if document exists but image isn't loading or error -->
             <div v-else-if="currentDocument" class="image-error-placeholder grey--text">
               <v-icon color="grey" large>mdi-image-search</v-icon>
               <p class="mt-2">Image unavailable or pending load</p>
               <p v-if="preloadQueue[currentDocument.id]" class="text-caption">Status: {{ preloadQueue[currentDocument.id] }}</p>
               <p v-else class="text-caption">Status: Not yet requested</p>
            </div>
          </div>

          <!-- Instructions -->
          <div class="instructions mt-4">
            <p>Use keyboard:
              <span class="mx-2"><v-icon small>mdi-arrow-left-bold</v-icon> Left = <strong>BAD</strong></span>
              <span class="mx-2"><v-icon small>mdi-arrow-right-bold</v-icon> Right = <strong>GOOD</strong></span>
              <span class="mx-2"><v-icon small>mdi-arrow-down-bold</v-icon> Down = <strong>Skip</strong></span>
            </p>
          </div>

        </div>
        <!-- Displayed when loading OR no documents left/found after loading -->
        <div v-else-if="!loading && !currentDocument" class="text-center mt-5 pa-5">
          <v-icon large color="grey" class="mb-3">mdi-information-outline</v-icon>
          <p v-if="allLoaded && filteredTotalCounts[tab] === 0">No documents were found in the '{{ getTabNoun(tab) }}' category.</p>
          <p v-else-if="allLoaded">No more documents to review in the '{{ getTabNoun(tab) }}' category.</p>
          <!-- Distinguish between truly no docs and initial loading state -->
          <p v-else-if="!documents.length && filteredTotalCounts[tab] !== 'Loading...' && filteredTotalCounts[tab] > 0">No documents loaded yet for '{{ getTabNoun(tab) }}'.</p>
          <p v-else-if="!documents.length && filteredTotalCounts[tab] === 0">No documents found in the '{{ getTabNoun(tab) }}' category.</p>
          <p v-else>Loading or finished.</p> <!-- Catch-all -->
        </div>
         <div v-else-if="loading" class="text-center mt-5 pa-5">
             <v-progress-circular indeterminate color="primary"></v-progress-circular>
             <p class="mt-3">Loading data...</p>
         </div>
      </div>
    </div>

    <!-- Snackbar for feedback -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      timeout="2000"
      top
      right
    >
      {{ snackbar.message }}
      <template v-slot:action="{ attrs }">
        <v-btn
          color="white"
          text
          v-bind="attrs"
          @click="snackbar.show = false"
        >
          Close
        </v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import {
  getFirestore,
  collection,
  getDocs,
  setDoc,
  doc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  getCountFromServer,
} from "firebase/firestore";
import { getStorage, ref, getDownloadURL } from "firebase/storage";

const DB_COLLECTION = "op_switchorders_otos";
const ORDER_BY_FIELD = "job_id"; // Ensure index exists in Firestore

// Flag to prevent concurrent executions of loadNextBatch if somehow called rapidly
let isLoadNextBatchRunning = false;

export default {
  name: "SwitchOrdersReview",
  data() {
    // --- Keep your existing data properties ---
    return {
      tab: 3,
      documents: [],
      currentIndex: 0,
      preloadQueue: {},
      preloadCount: 5,
      batchSize: 20,
      lastDoc: null,
      loading: false, // This will now be primarily controlled BY loadNextBatch
      allLoaded: false,
      imageError: false,
      snackbar: { show: false, message: '', color: '' },
      totalCount: 0,
      filteredTotalCounts: { 0: null, 1: null, 2: null, 3: null },
    };
  },
  computed: {
    // --- Keep your existing computed properties ---
    currentDocument() {
       if (this.currentIndex >= 0 && this.currentIndex < this.documents.length) {
           const doc = this.documents[this.currentIndex];
           return doc;
       }
       return null;
    },
    currentImage() {
      if (!this.currentDocument) {
         return null;
      }
      const docId = this.currentDocument.id;
      const statusOrUrl = this.preloadQueue[docId];
      if (statusOrUrl && statusOrUrl !== 'loading' && statusOrUrl !== 'error') {
            return statusOrUrl;
      }
      return null;
    },
  },
  watch: {
    // --- Keep your existing watcher ---
    'currentDocument.id': function(newId, oldId) {
      console.log(`Watcher(currentDocument.id): Changed from ${oldId} to ${newId}`);
      this.imageError = false;

      if (newId) {
          if (!this.preloadQueue[newId]) {
              console.log(`Watcher: Current document ${newId} not in preloadQueue. Triggering loadImageForDocument.`);
              this.loadImageForDocument(this.currentDocument);
          } else {
               console.log(`Watcher: Current document ${newId} already has status: ${this.preloadQueue[newId]}`);
               if(this.preloadQueue[newId] === 'error') {
                    this.imageError = true;
               }
          }
          console.log(`Watcher: Triggering preloadImages for subsequent docs.`);
          this.preloadImages();
      } else {
          console.log("Watcher: Current document became null, no image loading needed.");
      }
    }
  },
  methods: {
    // --- Keep initialize, buildQueryConstraints, loadTotalCount, loadFilteredTotalCount, getCountText, getTabNoun ---
    async initialize() {
      console.log("METHOD: initialize() called");
      // Let loadNextBatch handle the loading flag for the initial load
      // this.loading = true; // Remove this
      await this.loadTotalCount();
      await this.loadFilteredTotalCount(this.tab);
      await this.loadNextBatch(); // This will set/unset loading
      // this.loading = false; // Remove this
      console.log("METHOD: initialize() finished.");
    },

    buildQueryConstraints(forCount = false) {
        const constraints = [];
        switch (this.tab) {
            case 0: constraints.push(where("review", "==", true)); break;
            case 1: constraints.push(where("classification", "==", "GOOD")); break;
            case 2: constraints.push(where("classification", "==", "BAD")); break;
            case 3: constraints.push(where("classification", "==", "unclassified")); break;
        }
        constraints.push(orderBy(ORDER_BY_FIELD));
        if (!forCount) {
            if (this.lastDoc) {
            constraints.push(startAfter(this.lastDoc));
            }
            constraints.push(limit(this.batchSize));
        }
        return constraints;
    },

    async loadTotalCount() {
        console.log("METHOD: loadTotalCount() called.");
        try {
            const db = getFirestore();
            const q = query(collection(db, DB_COLLECTION));
            const snapshot = await getCountFromServer(q);
            this.totalCount = snapshot.data().count;
            console.log("METHOD: loadTotalCount() - Total count:", this.totalCount);
        } catch (error) {
            console.error("Error loading total count:", error);
            this.totalCount = 'Error';
        }
    },

     async loadFilteredTotalCount(tabIndex) {
      if (this.filteredTotalCounts[tabIndex] !== null && this.filteredTotalCounts[tabIndex] !== 'Error') {
         if(this.filteredTotalCounts[tabIndex] === 'Loading...') {
            console.log(`METHOD: loadFilteredTotalCount(${tabIndex}) skipped, already loading.`);
            return;
         }
         if(typeof this.filteredTotalCounts[tabIndex] === 'number') {
            console.log(`METHOD: loadFilteredTotalCount(${tabIndex}) skipped, count already loaded: ${this.filteredTotalCounts[tabIndex]}`);
            return;
         }
      }
      console.log(`METHOD: loadFilteredTotalCount(${tabIndex}) called.`);
      // Use a temporary loading state for count if needed, but don't block data load
      // this.$set(this.filteredTotalCounts, tabIndex, 'Loading...'); // Optional: still show '...' in chip

      try {
        const db = getFirestore();
        const qConstraints = this.buildQueryConstraints(true);
        const q = query(collection(db, DB_COLLECTION), ...qConstraints);
        const snapshot = await getCountFromServer(q);
        const count = snapshot.data().count;
        this.$set(this.filteredTotalCounts, tabIndex, count);
        console.log(`METHOD: loadFilteredTotalCount(${tabIndex}) - Filtered count:`, count);
      } catch (error) {
        console.error(`Error loading filtered count for tab ${tabIndex}:`, error);
        this.$set(this.filteredTotalCounts, tabIndex, 'Error');
      }
    },

    getCountText(tabIndex) {
      const count = this.filteredTotalCounts[tabIndex];
      if (count === null) return '?';
      if (count === 'Loading...') return '...';
      if (count === 'Error') return 'ERR';
      return count;
    },

    getTabNoun(tabIndex) {
      switch (tabIndex) {
        case 0: return "Exception";
        case 1: return "GOOD";
        case 2: return "BAD";
        case 3: return "Unclassified";
        default: return "Unknown";
      }
    },


    // --- *** REVISED loadNextBatch *** ---
    async loadNextBatch() {
      // Prevent multiple simultaneous executions OF THIS FUNCTION
      if (isLoadNextBatchRunning) {
          console.log(`METHOD: loadNextBatch() skipped. Already running.`);
          return;
      }
      // Prevent loading if all data for the filter is already loaded
      if (this.allLoaded) {
           console.log(`METHOD: loadNextBatch() skipped. AllLoaded: ${this.allLoaded}`);
           return;
      }

      isLoadNextBatchRunning = true; // Mark as running
      this.loading = true; // <<< SET loading flag HERE
      console.log(`METHOD: loadNextBatch() called for tab ${this.tab}. Starting after: ${this.lastDoc?.id || 'beginning'}`);
      const wasEmpty = this.documents.length === 0;

      try {
        const db = getFirestore();
        const qConstraints = this.buildQueryConstraints();
        const q = query(collection(db, DB_COLLECTION), ...qConstraints);

        const snapshot = await getDocs(q);
        const newDocs = snapshot.docs.map(docSnap => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));

        console.log(`METHOD: loadNextBatch() - Loaded ${newDocs.length} new documents.`);

        if (newDocs.length === 0 || newDocs.length < this.batchSize) {
          this.allLoaded = true;
          console.log("METHOD: loadNextBatch() - All documents loaded for this filter or no more match.");
        }
        if (snapshot.docs.length > 0) {
          this.lastDoc = snapshot.docs[snapshot.docs.length - 1];
        }

        this.documents.push(...newDocs);
        console.log(`METHOD: loadNextBatch() - Documents array length now: ${this.documents.length}`);

        // --- Trigger first image load ---
        if (wasEmpty && this.documents.length > 0) {
            console.log("METHOD: loadNextBatch() - First batch loaded for this tab.");
             if (this.currentIndex !== 0) {
                console.warn("METHOD: loadNextBatch() - Resetting currentIndex to 0 as first batch was loaded.");
                this.currentIndex = 0;
             }
            const firstDoc = this.documents[0];
             if (firstDoc && !this.preloadQueue[firstDoc.id]) {
                 console.log(`METHOD: loadNextBatch() - Explicitly calling loadImageForDocument for first doc: ${firstDoc.id}`);
                 this.loadImageForDocument(firstDoc);
             } else if (firstDoc) {
                 console.log(`METHOD: loadNextBatch() - First doc ${firstDoc.id} already has status: ${this.preloadQueue[firstDoc.id]}. Watcher should handle preloading.`);
                 if(this.preloadQueue[firstDoc.id] === 'error') {
                    this.imageError = true;
                 }
             } else {
                 console.warn("METHOD: loadNextBatch() - First batch loaded, but documents[0] is unexpectedly undefined.");
             }
        } else if (newDocs.length > 0) {
             console.log("METHOD: loadNextBatch() - Added new docs to existing list. Watcher handles preloading.");
             this.preloadImages(); // Trigger preload for subsequent images
        } else if (wasEmpty && newDocs.length === 0) {
             console.log("METHOD: loadNextBatch() - First batch query returned 0 documents.");
        }

      } catch (error) {
        console.error("Error loading batch documents:", error);
        this.showSnackbar("Error loading documents", "error");
        this.allLoaded = true; // Prevent further load attempts on error
      } finally {
        // Crucially, unset the loading flag and re-entrancy guard HERE
        this.loading = false; // <<< UNSET loading flag HERE
        isLoadNextBatchRunning = false;
        console.log(`METHOD: loadNextBatch() finished. Loading state is now: ${this.loading}`);
      }
    },

    // --- Keep Image Handling methods (preloadImages, loadImageForDocument, onImageLoadSuccess, onImageError) ---
    async preloadImages() {
        const start = this.currentIndex + 1;
        const end = Math.min(start + this.preloadCount, this.documents.length);
        if (start >= this.documents.length || start >= end) {
             console.log(`METHOD: preloadImages() - No images to preload.`);
             return;
        }
        console.log(`METHOD: preloadImages() - Preloading from index ${start} to ${end - 1}`);
        const preloadPromises = [];
        for (let i = start; i < end; i++) {
            const docToPreload = this.documents[i];
            if (docToPreload && !this.preloadQueue[docToPreload.id]) {
                console.log(`METHOD: preloadImages() - Queueing preload for doc: ${docToPreload.id}`);
                this.$set(this.preloadQueue, docToPreload.id, 'loading');
                preloadPromises.push(this.loadImageForDocument(docToPreload));
            } else if(docToPreload) {
                console.log(`METHOD: preloadImages() - Skipping preload for doc ${docToPreload.id}, status: ${this.preloadQueue[docToPreload.id]}`);
            }
        }
        console.log(`METHOD: preloadImages() - Initiated ${preloadPromises.length} preload requests.`);
    },

     async loadImageForDocument(document) {
       if (!document || !document.id) {
           console.warn("METHOD: loadImageForDocument() called without a valid document.", document);
           return;
       }
       const docId = document.id;
       const pageId = document.page_id;

       if (this.preloadQueue[docId] && this.preloadQueue[docId] !== 'loading') {
           return;
       }
       if (this.preloadQueue[docId] !== 'loading') {
            console.log(`METHOD: loadImageForDocument(${docId}) - Setting status to 'loading'.`);
           this.$set(this.preloadQueue, docId, 'loading');
       }

       if (this.currentDocument?.id === docId) {
          this.imageError = false;
       }

       if (!pageId) {
           console.warn(`METHOD: loadImageForDocument(${docId}) - Document has no page_id. Cannot load image.`);
           this.$set(this.preloadQueue, docId, 'error');
           if (this.currentDocument?.id === docId) this.imageError = true;
           return;
       }

       const path = `operations/switchorders/pages/${pageId}.jpg`;
       console.log(`METHOD: loadImageForDocument(${docId}) - Requesting URL for page: ${pageId}, path: ${path}`);

       try {
         const storage = getStorage();
         const storageRef = ref(storage, path);
         const url = await getDownloadURL(storageRef);
         this.$set(this.preloadQueue, docId, url);
         console.log(`METHOD: loadImageForDocument(${docId}) - Successfully got image URL.`);
         if (this.currentDocument?.id === docId) {
            this.imageError = false;
         }
       } catch (error) {
         console.error(`METHOD: loadImageForDocument(${docId}) - Error getting download URL for path ${path}:`, error.code, error.message);
         this.$set(this.preloadQueue, docId, 'error');
         if (this.currentDocument?.id === docId) {
             console.log(`METHOD: loadImageForDocument(${docId}) - Setting imageError = true due to load failure.`);
             this.imageError = true;
         }
       }
     },

     onImageLoadSuccess(docId) {
        console.log(`EVENT: v-img loaded successfully for doc: ${docId}`);
        if (this.currentDocument?.id === docId) {
             this.imageError = false;
        }
     },

     onImageError(docId) {
        console.error(`EVENT: v-img reported error displaying image for doc: ${docId}`);
        if (this.currentDocument?.id === docId) {
             this.imageError = true;
        }
        if (docId && this.preloadQueue[docId] !== 'error') { // Ensure docId is valid
             this.$set(this.preloadQueue, docId, 'error');
        }
     },

    // --- Keep Classification & Navigation methods (updateClassification, skipDocument, checkAndLoadMore, handleKeydown) ---
    async updateClassification(classification) {
      if (!this.currentDocument || this.loading) {
           console.warn("METHOD: updateClassification() skipped.", { hasDoc: !!this.currentDocument, isLoading: this.loading });
           return;
      }
      const docToUpdate = this.currentDocument;
      const docId = docToUpdate.id;
      const originalTab = this.tab;
      const indexToRemove = this.currentIndex;

      console.log(`METHOD: updateClassification() - Updating doc ${docId} to ${classification}. Current index: ${indexToRemove}`);
      this.loading = true; // Prevent clicks/keypresses during save

      try {
        const db = getFirestore();
        const docRef = doc(db, DB_COLLECTION, docId);
        const updateData = {
            classification: classification,
            review: classification === 'GOOD' || classification === 'BAD' ? false : docToUpdate.review
        };
        await setDoc(docRef, updateData, { merge: true });
        console.log(`METHOD: updateClassification() - Firestore updated for ${docId}`);

        // Update Local State
        if (this.filteredTotalCounts[originalTab] !== null && typeof this.filteredTotalCounts[originalTab] === 'number' && this.filteredTotalCounts[originalTab] > 0) {
             this.$set(this.filteredTotalCounts, originalTab, this.filteredTotalCounts[originalTab] - 1);
        }
        let targetTabIndex = -1;
        if (classification === 'GOOD') targetTabIndex = 1;
        else if (classification === 'BAD') targetTabIndex = 2;
         if (targetTabIndex !== -1 && targetTabIndex !== originalTab && this.filteredTotalCounts[targetTabIndex] !== null && typeof this.filteredTotalCounts[targetTabIndex] === 'number') {
             this.$set(this.filteredTotalCounts, targetTabIndex, this.filteredTotalCounts[targetTabIndex] + 1);
         }

        if (indexToRemove >= 0 && indexToRemove < this.documents.length) {
             this.documents.splice(indexToRemove, 1);
             console.log(`METHOD: updateClassification() - Removed doc ${docId} from local array at index ${indexToRemove}. New length: ${this.documents.length}`);
        } else {
             console.warn(`METHOD: updateClassification() - Could not find index ${indexToRemove} to remove doc ${docId}. Array length: ${this.documents.length}`);
        }

        if (this.currentIndex >= this.documents.length) {
            this.currentIndex = Math.max(0, this.documents.length - 1);
            console.log(`METHOD: updateClassification() - Adjusted currentIndex after removal (was last item). New index: ${this.currentIndex}`);
        } else {
             console.log(`METHOD: updateClassification() - Index ${this.currentIndex} still valid after removal.`);
        }
        this.showSnackbar(`Marked as ${classification}`, classification === "GOOD" ? "success" : "error");
        this.checkAndLoadMore();

      } catch (error) {
        console.error(`Error updating classification for ${docId}:`, error);
        this.showSnackbar("Error saving update", "error");
      } finally {
          this.loading = false; // Release loading lock
          console.log(`METHOD: updateClassification() finished for ${docId}. Loading state: ${this.loading}`);
      }
    },

     skipDocument() {
        if (!this.currentDocument || this.loading) {
            console.warn("METHOD: skipDocument() skipped.", { hasDoc: !!this.currentDocument, isLoading: this.loading });
            return;
        }
        const skippedDocId = this.currentDocument.id;
        console.log(`METHOD: skipDocument() - Skipping doc ${skippedDocId} at index ${this.currentIndex}`);
        this.currentIndex++;
        console.log(`METHOD: skipDocument() - Advanced currentIndex to: ${this.currentIndex}`);

        if (this.currentIndex >= this.documents.length) {
            console.log("METHOD: skipDocument() - Reached end of currently loaded documents.");
            if (!this.allLoaded) {
                 console.log("METHOD: skipDocument() - Attempting to load next batch...");
                 this.loadNextBatch(); // Async load more. loadNextBatch handles loading flag.
            } else {
                console.log("METHOD: skipDocument() - All documents loaded, no more items to skip to.");
            }
        } else {
            console.log(`METHOD: skipDocument() - Still have documents in buffer. New doc ID should be: ${this.documents[this.currentIndex]?.id}`);
            this.checkAndLoadMore(); // Check if we need to fetch next batch preemptively
        }
         this.showSnackbar("Skipped", "info");
    },

     checkAndLoadMore() {
        const remainingInBuffer = this.documents.length - (this.currentIndex + 1);
        const bufferThreshold = Math.max(2, Math.ceil(this.preloadCount / 2));
        console.log(`METHOD: checkAndLoadMore() - Checking buffer. Remaining: ${remainingInBuffer}, Threshold: ${bufferThreshold}`);

        // Check the re-entrancy guard too, don't trigger if already loading
        if (!isLoadNextBatchRunning && !this.allLoaded && remainingInBuffer < bufferThreshold) {
            console.log(`METHOD: checkAndLoadMore() - Buffer low, triggering loadNextBatch.`);
            this.loadNextBatch(); // Load more in background
        }
    },

     handleKeydown(event) {
       if (event.ctrlKey || event.metaKey || event.altKey || event.shiftKey) return;
       const targetTagName = event.target.tagName;
       if (targetTagName === 'INPUT' || targetTagName === 'TEXTAREA' || targetTagName === 'SELECT') return;
       if (!this.currentDocument || this.loading) { // Check loading flag
           return;
       }
       switch (event.key) {
         case "ArrowLeft": event.preventDefault(); console.log("Keydown: ArrowLeft -> BAD"); this.updateClassification("BAD"); break;
         case "ArrowRight": event.preventDefault(); console.log("Keydown: ArrowRight -> GOOD"); this.updateClassification("GOOD"); break;
         case "ArrowDown": event.preventDefault(); console.log("Keydown: ArrowDown -> Skip"); this.skipDocument(); break;
       }
     },

    // --- *** REVISED changeTab *** ---
    changeTab(newTabIndex) {
      // Note: v-model linked to 'tab' updates this.tab automatically
      console.log(`METHOD: changeTab() called. New tab index: ${newTabIndex} (Current this.tab: ${this.tab})`);

      // If a load is *already* in progress (e.g. user clicks tabs rapidly), maybe cancel it?
      // For now, we just reset state and start the new load. Check if `isLoadNextBatchRunning` is true?
      if (isLoadNextBatchRunning) {
          console.warn("METHOD: changeTab() called while loadNextBatch was potentially running. Resetting state anyway.");
          // Might need cancellation logic here in complex scenarios
      }

      console.log("METHOD: changeTab() - Resetting state for new tab.");
      // Reset state - crucially, DO NOT set this.loading = true here.
      this.documents = [];
      this.currentIndex = 0;
      this.lastDoc = null;
      this.allLoaded = false;
      this.preloadQueue = {};
      this.imageError = false;
      // this.loading = true; // <<<--- REMOVED THIS LINE

      // Load counts (if needed) and data for the new tab
      console.log(`METHOD: changeTab() - Triggering loadFilteredTotalCount(${this.tab}) and then loadNextBatch() for new tab.`);
      // loadNextBatch will set/unset the loading flag itself.
      this.loadFilteredTotalCount(this.tab) // Load count first (doesn't affect overall loading state)
        .then(() => {
             console.log(`METHOD: changeTab() - Count loaded (or skipped), now calling loadNextBatch()`);
             // loadNextBatch will now set this.loading = true internally when it starts
             return this.loadNextBatch();
         })
         .then(() => {
              // This .then() executes after loadNextBatch's promise resolves
              console.log(`METHOD: changeTab() - loadNextBatch() promise resolved.`);
              // Any logic needed *after* the first batch is successfully loaded can go here
         })
        .catch(error => {
            // Catch errors from either count load or (more likely) data load
            console.error(`METHOD: changeTab() - Error during tab change loading sequence:`, error);
            this.showSnackbar("Error loading data for tab", "error");
            this.allLoaded = true; // Prevent further load attempts on error
            this.loading = false; // Ensure loading is false on error too
        })
        .finally(() => {
             // This finally block executes after the entire chain (including loadNextBatch's finally)
             console.log(`METHOD: changeTab() - Promise chain finished. Current loading state should reflect loadNextBatch's final state: ${this.loading}`);
             // Log final state *after* everything should have settled
             console.log(`METHOD: changeTab() - Final state check: docs=${this.documents.length}, currentIdx=${this.currentIndex}, currentDocId=${this.currentDocument?.id}`);
        });
    },

    // --- Utility & Display ---
     showSnackbar(message, color = 'info') {
        this.snackbar.message = message;
        this.snackbar.color = color;
        this.snackbar.show = true;
    },

     isUnclassified(doc) {
         return doc.classification === 'unclassified';
    },

     displayClassification(doc) {
         if (!doc) return 'N/A';
         if (doc.classification === 'GOOD') return 'GOOD';
         if (doc.classification === 'BAD') return 'BAD';
         if (this.isUnclassified(doc)) return 'Unclassified';
         if (doc.classification === null) return 'Unclassified (null)';
         if (doc.classification === "") return 'Unclassified (empty)';
         return doc.classification ? String(doc.classification).toUpperCase() : 'Unknown';
    },

  }, // End methods

  // --- Lifecycle Hooks ---
  mounted() {
    console.log("LIFECYCLE: Component mounted.");
    window.addEventListener("keydown", this.handleKeydown);
    this.initialize(); // Start the loading process
  },

  beforeDestroy() {
    console.log("LIFECYCLE: Component destroying.");
    window.removeEventListener("keydown", this.handleKeydown);
  },
};
</script>

<style scoped>
  .the-thin-grey-border {
      border: 1px solid #e0e0e0; /* Light grey border */
      margin: 1px; /* Prevent border collapse */
  }

  .sidebar {
      min-width: 200px; /* Ensure sidebar has some width */
      border-right: 1px solid #e0e0e0;
      flex-basis: 200px; /* Give sidebar a base width */
      flex-grow: 0; /* Don't let sidebar grow */
      flex-shrink: 0; /* Don't let sidebar shrink */
  }

  .image-container {
    width: 85%; /* Max width of the container */
    max-width: 800px; /* Optional max pixel width */
    min-height: 400px; /* Reserve space */
    margin-left: auto;
    margin-right: auto;
    border: 1px solid #ccc; /* Border for the container */
    background-color: #f8f8f8; /* Light background */
    position: relative; /* For centering spinner/error */
    display: flex; /* Use flexbox for centering */
    align-items: center;
    justify-content: center;
    overflow: hidden; /* Hide potential image overflow before it loads/resizes */
  }

  .review-image {
    max-width: 100%; /* Image scales down */
    max-height: 100vh; /* Limit image height based on viewport */
    height: auto; /* Maintain aspect ratio */
    display: block; /* Remove extra space below image */
    object-fit: contain; /* Ensure image is contained within bounds */
  }

  .image-error-placeholder {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      text-align: center;
      padding: 20px;
      height: 100%; /* Fill container */
  }
  .image-error-placeholder .text-caption {
      word-break: break-all; /* Wrap long paths */
      font-size: 0.75rem;
      max-width: 90%; /* prevent overly wide path text */
  }


  .instructions {
    text-align: center;
    font-size: 0.9rem; /* Slightly smaller */
    color: #555;
  }
  .instructions p {
    margin: 5px 0;
  }
  .instructions strong {
      color: #000;
      font-weight: 600; /* Bolder */
  }
  .instructions .mx-2 {
      white-space: nowrap; /* Keep icon and text together */
      display: inline-block; /* Ensure margin works correctly */
  }

  /* Less prominent alerts if used */
  .classification-alert {
      width: auto; /* Don't force width */
      max-width: 75%;
      text-align: center;
      margin-left: auto;
      margin-right: auto;
      font-size: 0.85rem;
      padding: 4px 12px;
  }

  /* Ensure chips in tabs don't disrupt alignment too much */
  .v-tab .v-chip {
      height: 18px;
      font-size: 0.7rem;
      line-height: 1; /* Adjust line height for better vertical centering */
      margin-top: auto; /* Align chip nicely in tab */
      margin-bottom: auto;
  }
   .v-tabs .v-chip--x-small {
        height: 16px;
        font-size: 0.65rem;
        padding: 0 4px;
        line-height: 1;
   }

   /* Ensure flex-grow works correctly */
   .flex-grow-1 {
       flex-grow: 1;
   }

   /* Add some basic responsive adjustments */
    @media (max-width: 959px) { /* Vuetify's md breakpoint */
       .sidebar {
           min-width: auto;
           border-right: none; /* No right border when stacked */
       }
        .flex-grow-1.ml-md-4 {
             margin-left: 0 !important; /* Remove left margin on mobile */
        }
         .flex-grow-1.mt-md-0 {
              margin-top: 1rem !important; /* Add margin-top on mobile */
         }
   }

</style>